import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {getAssetUrl} from '@Utils/s3.util';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {Button, Size, Type} from '@Components/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from './download-panel-footer.module.scss';
import {isUserPremium} from '@Libraries/user.library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {MODALS_IDS} from '@Components/modal-container';
import {GA4EventName, GA4EventParamName, NEW_SUBSCRIPTION_START_SOURCE_COOKIE, NewSubscriptionStartSource, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {
  EXPENSIVE_PRODUCT_ID,
  FREEBIE_PRODUCT_ID,
  MAX_FREEBIE_DIMENSION,
  VideoDesignSizeOptionType,
  getDisplayPriceForProduct,
  getProductPriceForProduct,
} from '@Libraries/poster-download-library';
import {isIOSApp} from '@Utils/browser.util';
import {openPremiumPricingModal} from '@Modals/premium-pricing-modal/premium-pricing-modal.library';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {getWidthAndHeightInPixels} from '@Libraries/poster-object.library';
import {SizeDropdownOptions} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper.types';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import {COOKIE_EXPIRY_30_MINUTES, setCookie} from '@Utils/cookie.util';
import {Tooltip} from '@Components/tooltip';
import {useSavePosterBeforeDownloadIfImageUploading} from '@Hooks/download-dialog/useSavePosterBeforeDownloadIfImageUploading';
import {useGetIfHeavyOrderIsInProcess} from '@Hooks/download-dialog/useGetIfHeavyOrderIsInProcess';

enum MoreOptions {
  DOWNLOAD_TO_DRIVE = 'drive',
  DOWNLOAD_TO_DROPBOX = 'dropbox',
  RESET_TO_FREE_SETTINGS = 'reset_to_free_settings',
}

interface DownloadPanelFooterProps {
  panelId: string;
  className?: string;
  onClickDownload: () => void;
  onDownloadToDrive: () => Promise<void>;
  onDownloadToDropbox: () => Promise<void>;
  hasTopShadow?: boolean;
  showLoading?: boolean;
  isOpenedInPrintShop?: boolean;
  isOpenedInEditor?: boolean;
  isOpenedInModal?: boolean;
}

export function DownloadPanelFooter({
  className = '',
  isOpenedInEditor = true,
  isOpenedInPrintShop = false,
  showLoading = false,
  hasTopShadow = true,
  isOpenedInModal = false,
  ...props
}: DownloadPanelFooterProps): ReactElement {
  const dispatch = useAppDispatch();
  const downloadButtonRef = useRef<HTMLDivElement | null>(null);
  const [isDownloadBeingHovered, setIsDownloadBeingHovered] = useState<boolean>(false);
  const {
    meetsGettyRequirements,
    setScale,
    setSelectedDisplaySizeOption,
    posterObject,
    productsData,
    appleDownloadProducts,
    fileType,
    selectedProductId,
    setSelectedProductId,
    needsPurchase,
    upgradeAmount,
    upgradeDisplayAmount,
    appleSubscriptionUpgradePrice,
    duration,
    videoScale,
    setVideoScale,
    setDuration,
    selectedPages,
  } = useDownloadPanelData();
  const areImagesUploading = useAppSelector((state) => {
    return !!state.posterEditor.posterObject?.uploadingImages.length;
  });
  const isSaveCurrentlyDisabledAndPosterNotSavedBefore = (posterObject.hashedID === '' || posterObject.id === '') && areImagesUploading && isOpenedInEditor;
  const isHeavyOrderInProcess = useGetIfHeavyOrderIsInProcess();
  useSavePosterBeforeDownloadIfImageUploading(isOpenedInEditor, posterObject, props.panelId, isOpenedInModal);
  const EXPENSIVE_PRODUCT = productsData?.find((productsData) => productsData.id === String(EXPENSIVE_PRODUCT_ID));
  const SELECTED_PRODUCT = productsData?.find((productsData) => productsData.id === String(selectedProductId));

  const trackGAEvent = (id: MoreOptions): void => {
    trackPosterBuilderGA4Events(GA4EventName.SAVE_TO_DRIVE, {
      [GA4EventParamName.TYPE]: id,
    });
  };

  const resetToFreeSettingsForVideo = (): void => {
    setSelectedProductId(FREEBIE_PRODUCT_ID);
    if (duration > 20) {
      setDuration(20);
    }

    if (videoScale === VideoDesignSizeOptionType.UHD_4K || videoScale === VideoDesignSizeOptionType.FULL_HD) {
      setVideoScale(VideoDesignSizeOptionType.HD);
      const {width: convertedWidth, height: convertedHeight} = getWidthAndHeightInPixels(posterObject);
      const scaledDimension = 1280;
      if (convertedWidth < convertedHeight) {
        setScale(scaledDimension / convertedHeight);
      } else {
        setScale(scaledDimension / convertedWidth);
      }
    }
  };
  const resetToFreeSettingsForImage = (): void => {
    const {width, height} = getWidthAndHeightInPixels(posterObject);
    if (Math.max(width, height) > MAX_FREEBIE_DIMENSION) {
      setSelectedDisplaySizeOption(SizeDropdownOptions.ORIGINAL_COMPRESSED);
      const newScale = MAX_FREEBIE_DIMENSION / Math.max(width, height);
      setScale(newScale);
    } else {
      setSelectedDisplaySizeOption(SizeDropdownOptions.ORIGINAL);
      setScale(1);
    }
  };
  const resetToFreeSettingsForGIF = (): void => {
    if (duration > 10) {
      setDuration(10);
    }
    const {width, height} = getWidthAndHeightInPixels(posterObject);
    if (Math.max(width, height) > MAX_FREEBIE_DIMENSION) {
      setSelectedDisplaySizeOption(SizeDropdownOptions.ORIGINAL_COMPRESSED);
      const newScale = MAX_FREEBIE_DIMENSION / Math.max(width, height);
      setScale(newScale);
    } else {
      setSelectedDisplaySizeOption(SizeDropdownOptions.SMALL);
      setScale(1);
    }
  };

  const resetToFreeSettings = (): void => {
    setSelectedProductId(FREEBIE_PRODUCT_ID);

    switch (fileType) {
      case FileTypeOptions.PNG:
      case FileTypeOptions.JPG:
        resetToFreeSettingsForImage();
        return;
      case FileTypeOptions.MP4:
        resetToFreeSettingsForVideo();
        return;
      case FileTypeOptions.GIF:
        resetToFreeSettingsForGIF();
        return;
      case FileTypeOptions.PDF:
      default:
        return;
    }
  };

  const onClick = (id: MoreOptions): void => {
    switch (id) {
      case MoreOptions.DOWNLOAD_TO_DRIVE:
        trackGAEvent(id);
        void props.onDownloadToDrive();
        break;

      case MoreOptions.DOWNLOAD_TO_DROPBOX:
        trackGAEvent(id);
        void props.onDownloadToDropbox();
        break;

      case MoreOptions.RESET_TO_FREE_SETTINGS:
        resetToFreeSettings();
        break;
    }

    if (isEditorMobileVariant()) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
  };

  const onClickDownload = async (): Promise<void> => {
    props.onClickDownload();
  };

  const getPopup = (): ReactElement => {
    return (
      <>
        <ClickableDiv
          id={MoreOptions.DOWNLOAD_TO_DRIVE}
          className={`flex-row-align-center spacing-p-3 hover-transition-all spacing-m-b-2 radius-4 ${styles.socialMediaOption}`}
          onClick={(): void => {
            onClick(MoreOptions.DOWNLOAD_TO_DRIVE);
          }}
        >
          <img className={styles.socialThumbs} src={getAssetUrl('google-drive-logo.png')} alt={window.i18next.t('pmwjs_save_to_google_drive')} />
          <Text val={window.i18next.t('pmwjs_save_to_google_drive')} className="content-body" size={TextSize.XSMALL} />
        </ClickableDiv>
        <ClickableDiv
          id={MoreOptions.DOWNLOAD_TO_DROPBOX}
          className={`flex-row-align-center spacing-p-3 hover-transition-all radius-4 ${isUserPremium() || fileType === FileTypeOptions.PDF || isOpenedInPrintShop ? '' : 'spacing-m-b-2'} ${styles.socialMediaOption}`}
          onClick={(): void => {
            onClick(MoreOptions.DOWNLOAD_TO_DROPBOX);
          }}
        >
          <img className={styles.socialThumbs} src={getAssetUrl('dropbox-logo.png')} alt={window.i18next.t('pmwjs_save_to_dropbox')} />
          <Text val={window.i18next.t('pmwjs_save_to_dropbox')} className="content-body" size={TextSize.XSMALL} />
        </ClickableDiv>
        {isUserPremium() || fileType === FileTypeOptions.PDF || isOpenedInPrintShop ? null : (
          <ClickableDiv
            id={MoreOptions.RESET_TO_FREE_SETTINGS}
            className={`flex-row-align-center hover-transition-all spacing-p-3 radius-4 ${styles.socialMediaOption}`}
            onClick={(): void => {
              onClick(MoreOptions.RESET_TO_FREE_SETTINGS);
            }}
          >
            <Icon icon="icon-rotate-ccw" className="content-body" size={IconSize.SIZE_ICON_20} />
            <Text val={window.i18next.t('pmwjs_reset_to_free_settings')} className="content-body" size={TextSize.XSMALL} />
          </ClickableDiv>
        )}
      </>
    );
  };

  const getDownloadButtonText = (): string => {
    if (isUserPremium() || selectedProductId === FREEBIE_PRODUCT_ID || isOpenedInPrintShop) {
      return window.i18next.t('pmwjs_download');
    }

    if (needsPurchase) {
      return window.i18next.t('pmwjs_download_x_price_capitalized', {
        price: getDisplayPriceForProduct(SELECTED_PRODUCT, appleDownloadProducts),
        className: styles.capitalizedText,
      });
    }

    return window.i18next.t('pmwjs_download');
  };

  const getEffectiveUpgradeAmount = (): number | undefined => {
    if (isIOSApp() && appleSubscriptionUpgradePrice) {
      return appleSubscriptionUpgradePrice.upgradeAmount;
    }
    return upgradeAmount;
  };

  const getEffectiveUpgradeDisplayAmount = (): string | undefined => {
    if (isIOSApp() && appleSubscriptionUpgradePrice) {
      return appleSubscriptionUpgradePrice.upgradeDisplayAmount;
    }
    return upgradeDisplayAmount;
  };

  const getSavingPercentageNumber = (): number => {
    const effectiveUpgradeAmount = getEffectiveUpgradeAmount();
    if (!EXPENSIVE_PRODUCT || !effectiveUpgradeAmount) {
      throw new Error('Missing data for calculating saving percentage');
    }
    const expensiveProductPrice = getProductPriceForProduct(EXPENSIVE_PRODUCT, appleDownloadProducts);
    const percentage = ((expensiveProductPrice - effectiveUpgradeAmount) / expensiveProductPrice) * 100;
    return Math.floor(percentage / 5) * 5;
  };

  const getSavingPercentage = (): string => {
    return `${getSavingPercentageNumber()}%`;
  };

  const getPromotionText = (): string => {
    const effectiveUpgradeAmount = getEffectiveUpgradeAmount();
    const effectiveUpgradeDisplayAmount = getEffectiveUpgradeDisplayAmount();

    if (
      selectedProductId === EXPENSIVE_PRODUCT_ID &&
      !!EXPENSIVE_PRODUCT &&
      !!effectiveUpgradeAmount &&
      getProductPriceForProduct(EXPENSIVE_PRODUCT, appleDownloadProducts) > effectiveUpgradeAmount &&
      getSavingPercentageNumber() >= 10
    ) {
      return window.i18next.t('pmwjs_download_promotion_saving_message', {
        saving: getSavingPercentage(),
        amount: effectiveUpgradeDisplayAmount,
      });
    }

    return window.i18next.t('pmwjs_download_promotion_message', {
      amount: effectiveUpgradeDisplayAmount,
    });
  };

  const getTextForGAEventType = (): string => {
    if (selectedProductId === EXPENSIVE_PRODUCT_ID && needsPurchase) {
      return 'video_costsmore';
    }

    return 'highquality_generalupgrade';
  };

  const getPromotion = (): ReactElement | null => {
    if (!productsData) {
      return null;
    }
    return (
      <div className={`${styles.promotion} radius-8 _full-width spacing-p-2 text-center`}>
        <Text
          onClick={(event) => {
            const target = event.target as HTMLElement;
            if (target.id === 'upgrade') {
              trackPosterBuilderGA4Events(GA4EventName.POST_DOWNLOAD_UPGRADE, {[GA4EventParamName.TYPE]: getTextForGAEventType()});
              setCookie(NEW_SUBSCRIPTION_START_SOURCE_COOKIE, NewSubscriptionStartSource.PAYG_DOWNLOAD_DIALOG, COOKIE_EXPIRY_30_MINUTES);
              openPremiumPricingModal(false);
            }
          }}
          dangerouslySetInnerHTML
          val={getPromotionText()}
          size={TextSize.XSMALL}
          className="content-secondary"
        />
      </div>
    );
  };

  const getDownloadDisabledTooltip = (text: string, top = '-48px', width = 'max-content'): ReactElement => {
    return (
      <Tooltip
        style={{
          top: top,
          left: '50%',
          transform: 'translateX(-50%)',
          width: width,
        }}
        id={'download-tooltip'}
        text={<Text size={TextSize.XXSMALL} val={text} />}
      />
    );
  };

  const getTooltip = (): ReactElement | null => {
    if (!downloadButtonRef?.current || !isDownloadBeingHovered) {
      return null;
    }

    if (isHeavyOrderInProcess) {
      return getDownloadDisabledTooltip(window.i18next.t('pmwjs_heavy_order_in_process'), 'calc(-100% - 48px)', '100%');
    }

    if (isSaveCurrentlyDisabledAndPosterNotSavedBefore) {
      return getDownloadDisabledTooltip(window.i18next.t('pmwjs_wait_while_upload_finishes'));
    }

    if (selectedPages.length === 0) {
      return getDownloadDisabledTooltip(window.i18next.t('pmwjs_select_one_or_more_pages_to_download'));
    }

    return null;
  };

  const getPanelFooter = (): ReactElement => {
    return (
      <div className={`spacing-p-4 flex-v-row ${styles.footerContainer} ${className} ${hasTopShadow ? styles.topBorder : ''}`}>
        {!isUserPremium() && !isOpenedInPrintShop ? getPromotion() : null}
        <div
          className="flex-h-row _full-width flex-items-center _position-relative"
          ref={downloadButtonRef}
          onMouseEnter={(): void => {
            setIsDownloadBeingHovered(true);
          }}
          onMouseLeave={(): void => {
            setIsDownloadBeingHovered(false);
          }}
        >
          <Button
            isLoading={showLoading}
            customClasses="_full-width"
            type={Type.PRIMARY}
            size={Size.SMALL}
            icon="icon-cloud-download"
            disabled={!meetsGettyRequirements || isSaveCurrentlyDisabledAndPosterNotSavedBefore || selectedPages.length === 0 || isHeavyOrderInProcess}
            onClick={(): void => {
              void onClickDownload();
            }}
            dangerouslySetTextAsHTML
            text={getDownloadButtonText()}
          />
          {getTooltip()}
          <Dropdown
            popupClassName={styles.popupClasses}
            position={DROPDOWN_POSITION.FIXED}
            alignment={ALIGNMENT_TYPE.BOTTOM_END}
            selector={
              <Icon
                isDisabled={!meetsGettyRequirements || isSaveCurrentlyDisabledAndPosterNotSavedBefore || selectedPages.length === 0 || isHeavyOrderInProcess}
                className={`spacing-m-l-2 ${styles.footerIcon}`}
                icon="icon-menu-dots"
                type={IconType.PRIMARY}
                shape={IconShape.SQUARE}
                size={IconSize.SIZE_ICON_20}
              />
            }
            popup={getPopup()}
            popUpHasCustomWidth
            popupSpacingClass="spacing-p-3"
            showModalForSmallScreen
            modalTitle={window.i18next.t('pmwjs_download_options')}
          />
        </div>
      </div>
    );
  };

  return getPanelFooter();
}
